import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { notifications } from '@mantine/notifications';
import { IconBrandFacebook, IconRefresh } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

const METRIC_LABELS: Record<string, string> = {
  page_impressions: 'Impressions',
  page_engaged_users: 'Engaged users',
  page_post_engagements: 'Post engagements',
  page_fans: 'Fans',
};

function PageDetail() {
  const { pageId: pageIdParam } = useParams({ from: '/vendor/pages/$pageId' });
  const pageId = Number(pageIdParam);
  const [period, setPeriod] = useState<'24h' | '7d' | '30d' | '90d'>('30d');

  const utils = trpc.useUtils();
  const detail = trpc.analytics.pages.detail.useQuery({ pageId, period });
  const refresh = trpc.analytics.pages.refresh.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Refreshed: ${r.inserted} points written`,
        color: 'green',
      });
      void utils.analytics.pages.detail.invalidate();
      void utils.analytics.pages.list.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        {detail.isPending ? (
          <Skeleton height={120} />
        ) : detail.data ? (
          <Card>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Group>
                <Avatar radius="xl" size="lg" src={detail.data.page.profilePictureUrl ?? undefined}>
                  <IconBrandFacebook size={20} />
                </Avatar>
                <Stack gap={0}>
                  <Title order={3}>{detail.data.page.pageName}</Title>
                  <Text c="dimmed" size="xs" ff="monospace">
                    {detail.data.page.pageId}
                  </Text>
                  <Badge variant="light" size="xs" mt={4}>
                    {detail.data.page.followersCount.toLocaleString()} followers
                  </Badge>
                </Stack>
              </Group>
              <Group>
                <SegmentedControl
                  value={period}
                  onChange={(v) => setPeriod(v as typeof period)}
                  data={[
                    { value: '7d', label: '7d' },
                    { value: '30d', label: '30d' },
                    { value: '90d', label: '90d' },
                  ]}
                />
                <Button
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  loading={refresh.isPending}
                  onClick={() => refresh.mutate({ pageId })}
                >
                  Refresh from Meta
                </Button>
              </Group>
            </Group>
          </Card>
        ) : null}

        {detail.data && detail.data.series.length === 0 && (
          <Card>
            <Text c="dimmed" ta="center" py="md">
              No insight data cached yet. Click <strong>Refresh from Meta</strong> to prime the
              cache for this page.
            </Text>
          </Card>
        )}

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {Object.keys(METRIC_LABELS).map((metric) => (
            <Card key={metric}>
              <Stack gap="xs">
                <Title order={5}>{METRIC_LABELS[metric]}</Title>
                {detail.isPending ? (
                  <Skeleton height={180} />
                ) : (detail.data?.series ?? []).length === 0 ? (
                  <Text c="dimmed" size="sm">
                    —
                  </Text>
                ) : (
                  <LineChart
                    h={180}
                    data={(detail.data?.series ?? []) as Array<Record<string, number | string>>}
                    dataKey="date"
                    series={[{ name: metric, color: 'blue.6' }]}
                    withDots={false}
                    curveType="monotone"
                  />
                )}
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/pages/$pageId')({ component: PageDetail });
